import {
  AUTOTRADE_HEADER,
  TRADE_HEADER
} from '@bo-constants/playerHistoryDrawerHeaders';
import {
  PlayerImage,
  PlayerName,
  PlayerProfile,
  PlayerSecondaryHeader
} from '@library/components/player';
import { AutoTradeHistoryDrawerHeader } from '@bo-components/autoTradeHistoryDrawer/AutoTradeHistoryDrawerHeader';
import { AutotradeHistoryRow } from '@bo-components/autoTradeHistoryDrawer/AutotradeHistoryRow';
import { Drawer } from '@library/components/navigation/Drawer';
import { Select } from '@library/components/selects';
import { Tab } from '@library/components/buttons';
import { TradeHistoryRow } from '@bo-components/autoTradeHistoryDrawer/TradeHistoryRow';
import { TradeHistoryTable } from '@bo-components/autoTradeHistoryDrawer/TradeHistoryTable';
import { useAutoTradeHistoryDrawerData } from '@bo-hooks/autoTradeHistoryDrawer/useAutoTradeHistoryDrawerData';
import { useState } from 'react';

export interface AutoTradeHistoryDrawerProps {
  drawerOpen: boolean;
  durationId: string;
  fromDateString: string;
  gameDescriptor: string;
  gameId: string;
  leagueId?: string;
  playerId?: string;
  onCancelDrawer: () => void;
  playerImage: string;
  playerName: string;
  position: string;
}

export const AutoTradeHistoryDrawer = ({
  drawerOpen,
  durationId,
  fromDateString,
  gameDescriptor,
  gameId,
  onCancelDrawer,
  leagueId = undefined,
  playerId = undefined,
  playerImage,
  playerName,
  position
}: AutoTradeHistoryDrawerProps) => {
  const {
    handleSelectStatType,
    projection,
    statTypeSelectOptions,
    suggestedLines,
    tradeHistoryData
  } = useAutoTradeHistoryDrawerData({
    drawerOpen,
    durationId,
    fromDateString,
    gameId,
    leagueId,
    playerId
  });

  const handleCancelDrawer = () => {
    onCancelDrawer();
  };

  const [isTradeTabSelected, setIsTradeTabSelected] = useState(true);

  return (
    <Drawer
      direction="right"
      isOpen={drawerOpen}
      onCloseDrawer={handleCancelDrawer}
      size="xlarge"
    >
      <AutoTradeHistoryDrawerHeader onCancelModal={handleCancelDrawer} />

      <div className="px-4">
        <PlayerProfile
          className="border-soFresh-140 flex items-center justify-start gap-4 py-4"
          name={playerName}
          imageUrl={playerImage}
          secondaryHeader={position}
        >
          <PlayerImage className="bg-soFresh-120 size-12 rounded-lg" />
          <div className="flex flex-col">
            <PlayerName className="text-soClean-100 text-sm font-medium leading-tight tracking-tight" />
            <PlayerSecondaryHeader className="text-soClean-160 text-xs font-medium leading-5 tracking-tight" />
          </div>
        </PlayerProfile>

        <div className="flex flex-wrap gap-10">
          <div>
            <h3 className="text-soClean-100 font-heebo py-2 text-sm font-bold">
              GAME
            </h3>
            <h4 className="text-soClean-100 py-2 font-thin">
              {gameDescriptor}
            </h4>
          </div>
          <div>
            <h3 className="text-soClean-100 font-heebo py-2 text-sm font-bold">
              STAT TYPE
            </h3>
            <Select
              containerClassName="font-thin"
              className="min-w-52"
              optionsClassName="w-52 w-max"
              allOption={false}
              label="STAT TYPE"
              onChange={(statType) => handleSelectStatType(statType.value)}
              options={statTypeSelectOptions}
              size="large"
              value={projection?.projectionId ?? ''}
              zIndex={200}
            />
          </div>
          <div>
            <h3 className="text-soClean-100 font-heebo py-2 text-sm font-bold">
              PROJECTION VALUE
            </h3>
            <h4 className="text-soClean-100 py-2 font-thin">
              {projection?.projectionValue &&
              typeof projection?.projectionValue === 'number'
                ? projection?.projectionValue.toFixed(1)
                : ''}
            </h4>
          </div>
        </div>

        <div className="pb-1 pt-8">
          <Tab
            active={isTradeTabSelected}
            handleClick={() => {
              setIsTradeTabSelected(true);
            }}
            id="trade-history-tab"
            size="small"
            tabName="Autotrade History"
          />
          <Tab
            active={!isTradeTabSelected}
            handleClick={() => {
              setIsTradeTabSelected(false);
            }}
            id="autotrade-history-tab"
            size="small"
            tabName="Trade History"
          />
        </div>
        {isTradeTabSelected ? (
          <TradeHistoryTable
            headers={AUTOTRADE_HEADER}
            showEmpty={suggestedLines?.length === 0}
          >
            {suggestedLines?.map((row) => (
              <AutotradeHistoryRow
                key={`autotrade-history-row-${row.id}`}
                status={row.attributes.status}
                reason={row.attributes.trade_reason}
                projectionValue={row.attributes.line_score}
                probabilityOfMore={row.attributes.odd.probability_of_more}
                timestamp={row.attributes.applied_at}
                dataSource={row.attributes.data_source}
              />
            ))}
          </TradeHistoryTable>
        ) : (
          <TradeHistoryTable
            headers={TRADE_HEADER}
            showEmpty={tradeHistoryData?.length === 0}
          >
            {tradeHistoryData?.map((row) => (
              <TradeHistoryRow
                key={row.id}
                source={row.source}
                comment={row.comment}
                auditedChanges={row.auditedChanges}
                timestamp={row.timestamp}
              />
            ))}
          </TradeHistoryTable>
        )}
      </div>
    </Drawer>
  );
};
